import { Popover, Transition } from '@headlessui/react';
import SearchContext from 'contexts/SearchContext';
import { Filter as FilterIcon } from 'kzv-icons';
import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';

import { generateContextFilters, generateOptions, isArrayEqual } from './common';
import SearchFilter from './SearchFilter';
import { Mixpanel } from 'utils/Mixpanel';
import { setFilters, setPage } from 'contexts/SearchActions';
import Button from 'components/buttons/Button';

function SearchFilters() {
    const { state, dispatch } = useContext(SearchContext);
    const selectedFilters = state.filters.filter((f) => f.slug !== 'poi-type');
    const [selectedOptions, setSelectedOptions] = useState(selectedFilters);
    const [popoverOpened, setPopoverOpened] = useState(null);
    const availableFilters = state.availableFilters.filter((f) => f.slug !== 'poi-type');
    const buttonRef = useRef<HTMLButtonElement>();

    const onPopoverClose = () => {
        const existingFilters = state.filters.filter((f) => f.slug !== 'poi-type');
        const updatedFilters = generateContextFilters(selectedOptions);
        if (!isArrayEqual(updatedFilters, existingFilters)) {
            setPage(dispatch, 1);
            setFilters(dispatch, updatedFilters);
        }
    };

    const handleOptionChange = (isChecked, option) => {
        if (isChecked) {
            setSelectedOptions((existingOptions) => [...existingOptions, option]);
        } else {
            setSelectedOptions((existingOptions) => [...existingOptions.filter((opt) => opt.value !== option.value)]);
        }
    };

    // Save filters on popover close
    useEffect(() => {
        if (popoverOpened === false) {
            onPopoverClose();
        }
    }, [popoverOpened]);

    useEffect(() => {
        if (selectedOptions.length > 0) {
            Mixpanel.track('Search filters', {
                filters: selectedOptions.map((f) => f.value),
                view: state.mapView ? 'list' : 'map'
            });
        }
    }, [selectedOptions]);

    if (availableFilters?.length === 0) return null;

    return (
        <div className="">
            <Popover className="relative">
                {({ open }) => {
                    setPopoverOpened(open);
                    return (
                        <>
                            <Popover.Button
                                as={Button}
                                variant="outlined"
                                color="secondary"
                                ref={buttonRef}
                                startIcon={<FilterIcon height={18} strokeWidth={1} />}
                                styles={`text-sm shadow-sm ${selectedFilters.length > 0 ? 'border-brand' : ''}`}
                            >
                                Filtriraj
                            </Popover.Button>
                            <Transition
                                as={Fragment}
                                show={open}
                                enter="transition ease-out duration-200"
                                enterFrom="opacity-0 translate-y-1"
                                enterTo="opacity-100 translate-y-0"
                                leave="transition ease-in duration-150"
                                leaveFrom="opacity-100 translate-y-0"
                                leaveTo="opacity-0 translate-y-1"
                            >
                                <Popover.Panel static className="absolute z-20 w-screen max-w-sm px-4 mt-3 sm:px-0 ">
                                    <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                                        <div className="py-4 bg-white">
                                            <div className="space-y-8">
                                                {availableFilters?.map((filter, i) => (
                                                    <SearchFilter
                                                        key={i}
                                                        name={filter.name}
                                                        options={generateOptions(filter)}
                                                        slug={filter.slug}
                                                        handleChange={handleOptionChange}
                                                        selectedOptions={selectedOptions.filter(
                                                            (o) => o.slug === filter.slug
                                                        )}
                                                    />
                                                ))}
                                            </div>

                                            <div className="mt-4 pb-4 border-t border-gray-300" />
                                            <div className="flex justify-between px-4 items-center">
                                                <Button
                                                    variant="text"
                                                    color="secondary"
                                                    onClick={() => {
                                                        setSelectedOptions([]);
                                                        buttonRef.current?.click();
                                                    }}
                                                >
                                                    Ponastavi
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="secondary"
                                                    onClick={() => {
                                                        buttonRef.current?.click();
                                                    }}
                                                >
                                                    Shrani
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Popover.Panel>
                            </Transition>
                        </>
                    );
                }}
            </Popover>
        </div>
    );
}

export default SearchFilters;
